import { NgComponentOutlet } from "@angular/common";
import { Component, computed, effect, input, signal, Type } from "@angular/core";
import { NewUiButton } from "@ankaadia/ankaadia-shared-frontend";
import { derivedAsync } from "ngxtension/derived-async";
import { PrimeIcons } from "primeng/api";
import { DialogModule } from "primeng/dialog";
import { SettingsService } from "../../../shared/services/settings.service";
import { ChatWidgetDialogService } from "./chat-widget-dialog.service";

@Component({
  selector: "new-ui-chat-widget-dialog",
  imports: [DialogModule, NewUiButton, NgComponentOutlet],
  templateUrl: "./new-ui-chat-widget-dialog.component.html",
  styleUrl: "./chat-widget-dialog.component.scss",
})
export class NewUiChatWidgetDialogComponent {
  appendTo = input<any>("body");

  openWidget = this.chatWidgetDialogService.openWidget;

  contextForPreSelection = this.chatWidgetDialogService.contextForPreSelection;
  expandedView = signal<boolean>(true);
  isAuthorized = derivedAsync(() => this.settingsService.isAuthenticated$);

  // Workaround to reduce the initial bundle size
  newUiChatWidgetComponent = derivedAsync<Type<any>>(async () => {
    if (!this.openWidget() || !this.isAuthorized()) return null;
    return await import("../chat-widget/new-ui-chat-widget.component").then((x) => x.NewUiChatWidgetComponent);
  });

  chatWidgetComponent = derivedAsync<Type<any>>(async () => {
    if (!this.openWidget() || !this.isAuthorized()) return null;
    return await import("../chat-widget/chat-widget.component").then((x) => x.ChatWidgetComponent);
  });

  widgetStyle = computed(() => this.getWidgetStyle(this.expandedView()));

  setMobileView = effect(() => {
    if (!this.isAuthorized()) return;
    this.expandedView.set(!this.isMobileScreen());
  });

  constructor(
    private readonly chatWidgetDialogService: ChatWidgetDialogService,
    private readonly settingsService: SettingsService
  ) {}

  protected readonly PrimeIcons = PrimeIcons;

  openWidgetInNewWindow(): void {
    const style = this.getDialogStyleFromCurrentWindow() ?? this.getWidgetStyle(false);
    const newWindow = window.open(
      "/app/chats/widget",
      "_blank",
      `width=${style.width},height=${style.height},left=${style.left},top=${style.top},resizable,scrollbars`
    );

    if (newWindow) {
      this.chatWidgetDialogService.toggle();
      newWindow.focus();
    }
  }

  private isMobileScreen(): boolean {
    return window.innerWidth <= 768;
  }

  private getDialogStyleFromCurrentWindow(): DialogStyle {
    const dialog = document.querySelector(".chat-widget-dialog");
    if (!dialog) return null;

    const rect = dialog.getBoundingClientRect();
    const left = window.screenX + rect.left;
    const top = window.screenY + rect.top;
    const width = rect.width;
    const height = rect.height;

    return {
      width: `${width}px`,
      height: `${height}px`,
      top: `${top}px`,
      left: `${left}px`,
    };
  }

  private getWidgetStyle(expanded: boolean): DialogStyle {
    const dialogPadding = 50;
    const width = Math.min(window.innerWidth, expanded ? 1100 : 600) - dialogPadding;
    const height = Math.min(window.innerHeight, 900) - dialogPadding;

    return {
      width: `${width}px`,
      height: `${height}px`,
      maxHeight: `${height}px`,
      top: ``,
      right: ``,
    };
  }
}

interface DialogStyle {
  width?: string;
  height?: string;
  top?: string;
  left?: string;
  right?: string;
  maxHeight?: string;
}
